import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import type { InvoiceTemplate } from '~/api/invoiceTemplates/types';

import { useCreateInvoiceFromTemplate } from '~/api/invoices';
import { useDeleteInvoiceTemplate, usePauseInvoiceTemplate, useUnpauseInvoiceTemplate } from '~/api/invoiceTemplates';
import { AlertDialog } from '~/components';
import { SearchParamKeys } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { qs } from '~/utils/searchParams';
import { toast } from '~/utils/toast';

export const useOnShowInvoiceTemplate = (invoiceTemplate: InvoiceTemplate) => {
  const navigate = useNavigate();

  const onShow = useCallback(
    () => navigate(r(routes.showInvoiceTemplate, { invoiceTemplateId: invoiceTemplate.id })),
    [invoiceTemplate.id, navigate],
  );

  return useMemo(() => ({ onShow }), [onShow]);
};

export const useOnEditInvoiceTemplate = (invoiceTemplate: InvoiceTemplate) => {
  const navigate = useNavigate();

  const onEdit = useCallback(
    () => navigate(r(routes.editInvoiceTemplate, { invoiceTemplateId: invoiceTemplate.id })),
    [invoiceTemplate.id, navigate],
  );

  return useMemo(() => ({ onEdit }), [onEdit]);
};

export const useOnCreateInvoiceFromTemplate = (invoiceTemplate: InvoiceTemplate) => {
  const { isPending, mutate } = useCreateInvoiceFromTemplate(invoiceTemplate.id);

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  const onCreateInvoiceFromTemplate = useCallback(
    () =>
      mutate(undefined, {
        onSuccess: (invoice) =>
          navigate({
            pathname: r(routes.createInvoice, { invoiceId: invoice.id }),
            search: qs({ [SearchParamKeys.REDIRECT_PATH]: location.pathname }),
          }),
        onError: () => toast.danger({ description: t('common:error') }),
      }),
    [location.pathname, mutate, navigate, t],
  );

  return useMemo(() => ({ onCreateInvoiceFromTemplate, isPending }), [onCreateInvoiceFromTemplate, isPending]);
};

export const useOnPauseInvoiceTemplate = (invoiceTemplate: InvoiceTemplate) => {
  const { isPending, mutate } = usePauseInvoiceTemplate(invoiceTemplate.id);

  const { t } = useTranslation(['common']);

  const onPause = useCallback(() => mutate(undefined, { onError: () => toast.danger({ description: t('common:error') }) }), [mutate, t]);

  return useMemo(() => ({ onPause, isPending }), [onPause, isPending]);
};

export const useOnUnpauseInvoiceTemplate = (invoiceTemplate: InvoiceTemplate) => {
  const { isPending, mutate } = useUnpauseInvoiceTemplate(invoiceTemplate.id);

  const { t } = useTranslation(['common']);

  const onUnpause = useCallback(() => mutate(undefined, { onError: () => toast.danger({ description: t('common:error') }) }), [mutate, t]);

  return useMemo(() => ({ onUnpause, isPending }), [onUnpause, isPending]);
};

export const useOnDeleteInvoiceTemplate = (invoiceTemplate: InvoiceTemplate, options?: { redirect: boolean }) => {
  const { isPending, mutate } = useDeleteInvoiceTemplate(invoiceTemplate.id);

  const deleteHandle = AlertDialog.useHandle();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'invoiceTemplates']);

  const onDelete = useCallback(() => deleteHandle.open(null), [deleteHandle]);

  const confirmDelete = useCallback(() => {
    mutate(undefined, {
      onSuccess: () => {
        deleteHandle.close();
        toast.success({ description: t('invoiceTemplates:alerts.successDeleted') });

        if (options?.redirect) navigate(routes.invoiceTemplates);
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  }, [deleteHandle, mutate, navigate, options?.redirect, t]);

  return useMemo(() => ({ onDelete, confirmDelete, isPending, deleteHandle }), [confirmDelete, deleteHandle, isPending, onDelete]);
};
